import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPath, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { getTitleForHref } from "../config/navigation";

// Browser-like page tabs with KEEP-ALIVE: every tab is a live "frame" — its
// own <Router> with a frozen location, kept mounted (display:none) while
// inactive (see components/layout/TabFrames.jsx). Switching tabs only flips
// visibility, so forms, fetched data and scroll survive exactly as left.
//
// Tabs are created MANUALLY (+ button / Ctrl+Down). Normal navigation,
// including sidebar clicks, stays inside the active tab like a browser
// address bar. The real browser URL always mirrors the ACTIVE tab. F5/Ctrl+R
// remount only the active tab's frame (Ctrl+Shift+R = browser hard refresh).

const STORAGE_KEY = "erp-tabs";
const HOME_HREF = "/";

// Routes rendered WITHOUT AppLayout — never held in a tab. While the user is
// on one of these (prints, login...), frames stay mounted but hidden, so tab
// progress survives the excursion.
const TABLESS_PREFIXES = ["/login", "/register", "/maintenance", "/bnb", "/banamob", "/tools", "/print/", "/system/dashboards/"];
export const isTabless = (pathname) =>
  TABLESS_PREFIXES.some((p) =>
    p.endsWith("/") ? pathname.startsWith(p) : pathname === p || pathname.startsWith(p + "/")
  );

// Docs pages ARE tabbable and keep-alive, but they render FULL-SCREEN outside
// the ERP shell (their own DocsLayout, no sidebar). TabFrames renders their
// frames in a separate branch and hides the shell while a docs tab is active.
// Matches the home (/docs) and any sheet (/docs/:series/:doc).
export const isDocsHref = (pathname) => pathname === "/docs" || pathname.startsWith("/docs/");

// Tab strip (and shortcuts) only exist on desktop widths.
const isDesktop = () => typeof window === "undefined" || window.innerWidth >= 1024;

let tabSeq = 0;
const newId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `tab-${Date.now()}-${++tabSeq}`);

const parseHref = (href) => {
  let rest = href;
  let hash = "";
  const hashIdx = rest.indexOf("#");
  if (hashIdx >= 0) {
    hash = rest.slice(hashIdx);
    rest = rest.slice(0, hashIdx);
  }
  let search = "";
  const qIdx = rest.indexOf("?");
  if (qIdx >= 0) {
    search = rest.slice(qIdx);
    rest = rest.slice(0, qIdx);
  }
  return { pathname: rest || "/", search, hash };
};

const makeLocation = (href, state = null) => ({
  ...parseHref(href),
  state,
  key: newId().slice(0, 8),
});

const makeTab = (href, title) => ({
  id: newId(),
  href,
  title: title || getTitleForHref(href),
  location: makeLocation(href),
  nonce: 0, // bumped by reloadTab to remount this tab's frame
  locked: false, // pinned: can't be closed until unlocked (right-click / lock icon)
});

const loadStored = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY));
    const tabs = (parsed?.tabs || [])
      .filter((t) => t && typeof t.href === "string" && t.href.startsWith("/") && !isTabless(t.href.split("?")[0]))
      .map((t) => ({
        id: String(t.id || newId()),
        href: t.href,
        title: t.title || getTitleForHref(t.href),
        location: makeLocation(t.href),
        nonce: 0,
        locked: !!t.locked,
      }));
    if (!tabs.length) return null;
    const activeId = tabs.some((t) => t.id === parsed.activeId) ? parsed.activeId : tabs[0].id;
    return { tabs, activeId };
  } catch {
    return null;
  }
};

const TabsContext = createContext(null);

// Frames provide this so long-lived components (e.g. AppLayout polling) can
// pause work while their tab is hidden. Defaults to true for tabless pages.
export const FrameActivityContext = createContext(true);
export const useFrameActive = () => useContext(FrameActivityContext);

// Portal target inside the owning tab frame. Dialogs/sheets portal here
// instead of document.body so an open dialog hides and reappears WITH its
// tab. Null (default) = portal to body as usual (tabless/live pages).
export const FramePortalContext = createContext(null);
export const useFramePortal = () => useContext(FramePortalContext);

// The owning tab's id, exposed to the page tree inside each frame so a page can
// rename its own tab (e.g. the doc editor labels the tab with the sheet name).
// Null on tabless/live pages that aren't held in a frame.
export const FrameTabIdContext = createContext(null);
export const useFrameTabId = () => useContext(FrameTabIdContext);

export const TabsProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const currentHref = location.pathname + location.search;

  const [state, setState] = useState(() => {
    const stored = loadStored();
    if (stored) return stored;
    const path = window.location.pathname;
    const href = isTabless(path) ? HOME_HREF : path + window.location.search;
    const tab = makeTab(href);
    return { tabs: [tab], activeId: tab.id };
  });

  // Refs so callbacks/keyboard handlers never see stale state.
  const stateRef = useRef(state);
  stateRef.current = state;
  const hrefRef = useRef(currentHref);
  hrefRef.current = currentHref;
  const bootedRef = useRef(false);

  // Persist on every change (locations are rebuilt from href on load).
  useEffect(() => {
    try {
      const { tabs, activeId } = state;
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ tabs: tabs.map(({ id, href, title, locked }) => ({ id, href, title, locked })), activeId })
      );
    } catch {
      // storage full/blocked — tabs just won't survive reload
    }
  }, [state]);

  const activateTab = useCallback(
    (id) => {
      const tab = stateRef.current.tabs.find((t) => t.id === id);
      if (!tab) return;
      setState((prev) => (prev.activeId === id ? prev : { ...prev, activeId: id }));
      if (tab.href !== hrefRef.current) navigate(tab.href);
    },
    [navigate]
  );

  // Navigation coming from INSIDE a tab frame (Link clicks, useNavigate).
  // Updates that tab's frozen location; if it's the active tab, the real
  // browser URL follows. Tabless targets (docs/prints) leave the frame world.
  const navigateFrame = useCallback(
    (tabId, to, opts = {}) => {
      const href = typeof to === "string" ? to : createPath(to);
      const target = parseHref(href);
      if (isTabless(target.pathname)) {
        navigate(href, { replace: opts.replace, state: opts.state });
        return;
      }
      const loc = { ...target, state: opts.state ?? null, key: newId().slice(0, 8) };
      setState((prev) => ({
        ...prev,
        tabs: prev.tabs.map((t) =>
          t.id === tabId ? { ...t, href, title: getTitleForHref(href), location: loc } : t
        ),
      }));
      if (stateRef.current.activeId === tabId && href !== hrefRef.current) {
        navigate(href, { replace: opts.replace, state: opts.state });
      }
    },
    [navigate]
  );

  // + button / Ctrl+Down: new tab starts as a duplicate of the current page
  // ("two tabs, one page") — its frame mounts fresh, then lives independently.
  const newTab = useCallback(() => {
    const href = isTabless(window.location.pathname) ? HOME_HREF : hrefRef.current;
    const tab = makeTab(href);
    setState((prev) => ({ ...prev, tabs: [...prev.tabs, tab], activeId: tab.id }));
    if (href !== hrefRef.current) navigate(href);
  }, [navigate]);

  // Open a SPECIFIC href in a brand-new tab and switch to it (right-click →
  // "Open in new ERP tab"). Tabless targets (prints/bnb) can't live in a tab,
  // so they just navigate normally.
  const openInNewTab = useCallback(
    (href, title) => {
      if (!href) return;
      if (isTabless(parseHref(href).pathname)) {
        navigate(href);
        return;
      }
      const tab = makeTab(href, title);
      setState((prev) => ({ ...prev, tabs: [...prev.tabs, tab], activeId: tab.id }));
      if (href !== hrefRef.current) navigate(href);
    },
    [navigate]
  );

  const closeTab = useCallback(
    (id) => {
      const { tabs, activeId } = stateRef.current;
      const idx = tabs.findIndex((t) => t.id === id);
      if (idx === -1) return;
      if (tabs[idx].locked) return; // pinned — unlock before it can be closed
      const remaining = tabs.filter((t) => t.id !== id);
      // Never zero tabs: closing the last one resets to Dashboard.
      if (!remaining.length) {
        const tab = makeTab(HOME_HREF);
        setState({ tabs: [tab], activeId: tab.id });
        if (hrefRef.current !== HOME_HREF) navigate(HOME_HREF);
        return;
      }
      if (id !== activeId) {
        setState((prev) => ({ ...prev, tabs: remaining }));
        return;
      }
      const next = tabs[idx + 1] || tabs[idx - 1];
      setState({ tabs: remaining, activeId: next.id });
      if (next.href !== hrefRef.current) navigate(next.href);
    },
    [navigate]
  );

  // Close every tab except one (right-click → "Close other tabs"). Locked
  // (pinned) tabs are kept too — closing them needs an explicit unlock.
  const closeOthers = useCallback(
    (id) => {
      const { tabs, activeId } = stateRef.current;
      const keepId = id || activeId;
      const keep = tabs.find((t) => t.id === keepId);
      if (!keep) return;
      const kept = tabs.filter((t) => t.id === keepId || t.locked);
      const nextActive = kept.some((t) => t.id === activeId) ? activeId : keep.id;
      setState({ tabs: kept, activeId: nextActive });
      const active = kept.find((t) => t.id === nextActive);
      if (active && active.href !== hrefRef.current) navigate(active.href);
    },
    [navigate]
  );

  // Pin/unpin a tab. A locked tab shows a lock instead of its close button and
  // is skipped by closeTab / closeOthers until unlocked.
  const toggleTabLock = useCallback((id) => {
    setState((prev) => ({
      ...prev,
      tabs: prev.tabs.map((t) => (t.id === id ? { ...t, locked: !t.locked } : t)),
    }));
  }, []);

  const cycleTab = useCallback(
    (dir) => {
      const { tabs, activeId } = stateRef.current;
      if (tabs.length < 2) return;
      const idx = tabs.findIndex((t) => t.id === activeId);
      const next = tabs[(idx + dir + tabs.length) % tabs.length];
      activateTab(next.id);
    },
    [activateTab]
  );

  // Rename a tab from inside its own page (docs sheet → sheet name). Normal
  // navigation re-derives the title from the href, so this only sticks while
  // the tab stays on the page that set it — good enough for labelling sheets.
  const setTabTitle = useCallback((id, title) => {
    const t = (title || "").trim();
    if (!t) return;
    setState((prev) => {
      const tab = prev.tabs.find((x) => x.id === id);
      if (!tab || tab.title === t) return prev;
      return { ...prev, tabs: prev.tabs.map((x) => (x.id === id ? { ...x, title: t } : x)) };
    });
  }, []);

  // Drag-and-drop reorder (Chrome-style tab strip). Moves the tab with `id`
  // to sit at `toIndex` in the tab order.
  const moveTabToIndex = useCallback((id, toIndex) => {
    setState((prev) => {
      const tabs = [...prev.tabs];
      const fromIdx = tabs.findIndex((t) => t.id === id);
      if (fromIdx === -1) return prev;
      const clamped = Math.max(0, Math.min(tabs.length - 1, toIndex));
      if (clamped === fromIdx) return prev;
      const [moved] = tabs.splice(fromIdx, 1);
      tabs.splice(clamped, 0, moved);
      return { ...prev, tabs };
    });
  }, []);

  // Soft reload: remount only the active tab's frame. Session, contexts and
  // the other tabs stay untouched.
  const reloadTab = useCallback(() => {
    setState((prev) => ({
      ...prev,
      tabs: prev.tabs.map((t) => (t.id === prev.activeId ? { ...t, nonce: (t.nonce || 0) + 1 } : t)),
    }));
  }, []);

  // The real browser URL drives the ACTIVE tab (back/forward, deep links).
  // Frame-originated navigation already synced itself, so this no-ops then.
  // First tabbed arrival restores the last session when entered at "/".
  useEffect(() => {
    if (isTabless(location.pathname)) return;
    if (!isAuthenticated) return;

    if (!bootedRef.current) {
      bootedRef.current = true;
      const { tabs, activeId } = stateRef.current;
      const match = tabs.find((t) => t.href === currentHref);
      if (match) {
        if (activeId !== match.id) setState((prev) => ({ ...prev, activeId: match.id }));
        return;
      }
      if (currentHref === HOME_HREF) {
        const active = tabs.find((t) => t.id === activeId);
        if (active && active.href !== HOME_HREF) {
          // Entered at the root — restore the last active tab, like a browser
          // reopening its previous session.
          navigate(active.href, { replace: true });
          return;
        }
      }
    }

    setState((prev) => {
      const active = prev.tabs.find((t) => t.id === prev.activeId);
      if (active && active.href === currentHref) return prev;
      if (!active) {
        const tab = makeTab(currentHref);
        return { ...prev, tabs: [...prev.tabs, tab], activeId: tab.id };
      }
      const loc = { ...parseHref(currentHref), state: location.state ?? null, key: location.key || newId().slice(0, 8) };
      return {
        ...prev,
        tabs: prev.tabs.map((t) =>
          t.id === active.id ? { ...t, href: currentHref, title: getTitleForHref(currentHref), location: loc } : t
        ),
      };
    });
  }, [currentHref, location.pathname, location.state, location.key, isAuthenticated, navigate]);

  // Global shortcuts: Alt+Left/Right or Ctrl+Left/Right switch, Ctrl+Up close,
  // Ctrl+Down new, F5 / Ctrl+R soft-reload the active tab (Ctrl+Shift+R =
  // browser hard refresh), Ctrl+PageUp/PageDown cycle tabs (Excel's own
  // worksheet-tab shortcut, but Univer never binds Page Up/Down to anything,
  // so it's free to reuse here — and unlike Ctrl+Arrow it works on Docs pages
  // too, see below).
  //
  // Capture phase + stopPropagation on the tab combos: page arrow-key handlers
  // (useKeyboardRowNav, date flippers...) don't check modifiers, and every
  // kept-alive frame's window listener is still attached — without this one
  // keypress both switched the tab AND moved selections in the pages.
  useEffect(() => {
    const claim = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const onKeyDown = (e) => {
      if (!isDesktop() || isTabless(window.location.pathname)) return;

      // Alt+←/→: works everywhere — Docs pages and text fields included
      // (Univer only binds Alt+Enter; inputs give Alt+Arrow no meaning).
      // preventDefault also blocks the browser's own Alt+←/→ Back/Forward,
      // which the URL-sync effect above would pour into the ACTIVE tab,
      // overwriting it with another tab's page.
      if (e.altKey && !e.ctrlKey && !e.shiftKey && !e.metaKey && (e.key === "ArrowLeft" || e.key === "ArrowRight")) {
        claim(e);
        cycleTab(e.key === "ArrowRight" ? 1 : -1);
        return;
      }

      // Ctrl+PageUp/PageDown: the one Ctrl tab shortcut Docs pages DON'T claim
      // for themselves (Ctrl+Arrow is data-edge jump in the sheet; this isn't
      // used by Univer at all), so it's active there too.
      if (e.ctrlKey && !e.shiftKey && !e.altKey && !e.metaKey && (e.key === "PageUp" || e.key === "PageDown")) {
        claim(e);
        cycleTab(e.key === "PageDown" ? 1 : -1);
        return;
      }

      // Docs pages participate in tabs (click the strip to switch) but keep the
      // rest of the keyboard to themselves: Ctrl+←/→ is jump-to-data-edge in
      // the sheet, and F5/Ctrl+R should fall through to a plain reload.
      if (isDocsHref(window.location.pathname)) return;

      // Soft reload — works from anywhere, like a browser reload.
      const isPlainF5 = e.key === "F5" && !e.ctrlKey && !e.shiftKey && !e.altKey && !e.metaKey;
      const isCtrlR = e.key.toLowerCase() === "r" && e.ctrlKey && !e.shiftKey && !e.altKey && !e.metaKey;
      if (isPlainF5 || isCtrlR) {
        e.preventDefault();
        reloadTab();
        return;
      }

      if (!e.ctrlKey || e.shiftKey || e.altKey || e.metaKey) return;
      if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key)) return;
      // Deliberately active while typing / inside dialogs too — switching
      // tabs mid-edit (cross-verification) outranks Ctrl+arrow word-jump.
      claim(e);
      if (e.key === "ArrowRight") cycleTab(1);
      else if (e.key === "ArrowLeft") cycleTab(-1);
      else if (e.key === "ArrowUp") closeTab(stateRef.current.activeId);
      else if (e.key === "ArrowDown") newTab();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [cycleTab, closeTab, newTab, reloadTab]);

  const value = {
    tabs: state.tabs,
    activeId: state.activeId,
    activateTab,
    closeTab,
    closeOthers,
    toggleTabLock,
    newTab,
    openInNewTab,
    reloadTab,
    navigateFrame,
    setTabTitle,
    moveTabToIndex,
  };

  return <TabsContext.Provider value={value}>{children}</TabsContext.Provider>;
};

export const useTabs = () => {
  const ctx = useContext(TabsContext);
  if (!ctx) throw new Error("useTabs must be used within TabsProvider");
  return ctx;
};
